import { useState } from "react";
import { motion } from "framer-motion";
import { Eyebrow, Lines, Reveal } from "./Motion";
import { CITIES } from "@/lib/site";

const CORE = ["Johnson City", "Kingsport", "Bristol"];
const DOTS = {
  Kingsport: [150, 170], Bristol: [470, 128], Blountville: [318, 196], Gray: [248, 262],
  "Piney Flats": [392, 250], "Johnson City": [300, 338], Jonesborough: [168, 372], Elizabethton: [440, 372],
};
const RIVERS = [
  "M0 196 C 90 160, 170 190, 240 214 S 330 236, 372 262 S 420 300, 470 318",
  "M600 420 C 540 404, 500 384, 470 350 S 420 300, 372 262",
];

const Map = ({ active }) => (
  <svg viewBox="0 0 600 480" className="h-full w-full" data-testid="region-map">
    <path d="M0 108 L600 76" stroke="#3A3A3A" strokeWidth="1.5" strokeDasharray="6 8" />
    <text x="20" y="92" fill="#555" fontSize="11" fontFamily="JetBrains Mono, monospace" letterSpacing="3">VIRGINIA</text>
    <text x="20" y="130" fill="#555" fontSize="11" fontFamily="JetBrains Mono, monospace" letterSpacing="3">TENNESSEE</text>
    {RIVERS.map((d, i) => (
      <motion.path key={i} d={d} fill="none" stroke="#2C2C2C" strokeWidth={i ? 6 : 9} strokeLinecap="round"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2.2, delay: i * 0.3, ease: "easeInOut" }} />
    ))}
    <ellipse cx="378" cy="262" rx="34" ry="12" fill="#2C2C2C" transform="rotate(-18 378 262)" />
    {Object.entries(DOTS).map(([name, [x, y]]) => {
      const on = active === name;
      const core = CORE.includes(name);
      return (
        <g key={name}>
          {core && <circle cx={x} cy={y} r="10" fill="#FFFFFF" className="pulse-ring" opacity="0.4" />}
          <circle cx={x} cy={y} r={on ? 9 : core ? 6 : 4} fill={on || core ? "#FFFFFF" : "#8A8A8A"} style={{ transition: "r .4s" }} />
          <text x={x + 14} y={y + 4} fill={on || core ? "#F5F5F5" : "#8A8A8A"} fontSize={core ? 15 : 12} fontFamily="JetBrains Mono, monospace" style={{ transition: "fill .3s" }}>{name}</text>
        </g>
      );
    })}
  </svg>
);

export const Region = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="region" data-testid="section-tri-cities-coverage" className="relative overflow-hidden bg-[#121212] px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto grid max-w-[1500px] items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Eyebrow>Service area</Eyebrow>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tighter text-neutral-50 md:text-6xl">
            <Lines lines={["Local builder.", <span key="c" className="font-serif font-normal italic text-neutral-400">Tri-Cities through and through.</span>]} />
          </h2>
          <p className="mt-6 max-w-md text-neutral-400">
            Serving the Tri-Cities region of Northeast Tennessee and Southwest Virginia. We know the hills, the clay, the humidity and the local permit offices, and we build for all of it.
          </p>
          <div className="mt-10 flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <button key={c} data-testid={`region-city-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onMouseEnter={() => setActive(c)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(c)} onBlur={() => setActive(null)}
                className={`rounded-full border px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors ${active === c ? "border-white bg-white text-[#0A0A0A]" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="corner-ticks relative aspect-[5/4] border border-white/[0.08] bg-[#0E0E0E] p-6 blueprint-grid">
            <Map active={active} />
            <span className="absolute bottom-4 right-5 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-600">Holston · Watauga · Boone Lake / not to scale</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
